import type { datadogLogs } from '@datadog/browser-logs';
import type { datadogRum } from '@datadog/browser-rum';
import ExpoConstants from 'expo-constants';

import { getEnvironmentConfig } from '@/constants/config';
import { isWidget } from '@/utils/isWidget.web';
import type { TrackingInterface } from '@/utils/tracking/_types';

import { loadDatadogBrowserSdk } from './datadogSdkLoader.web';

// SQR-13012: the SDKs are loaded dynamically in ./datadogSdkLoader.web.
let rum: typeof datadogRum | null = null;
let logs: typeof datadogLogs | null = null;
let isDatadogInitialized = false;

const DATADOG_SITE = 'datadoghq.com' as const;
const FLAGSHIP_SERVICE = 'squire-mobile-flagship';
const WIDGET_SERVICE = 'squire-widget';

type WidgetDatadogApp = { applicationId: string; clientToken: string };

// Embedded sessions report under the widget RUM app so the host dashboards
// keep working; an empty widgetDatadogApps keeps flagship's own app.
const resolveWidgetApp = (): WidgetDatadogApp | undefined => {
  if (!isWidget) return undefined;
  const [first] = getEnvironmentConfig().widgetDatadogApps;
  return first;
};

export function DatadogTracker(
  clientToken: string,
  applicationId: string,
  env: string,
): TrackingInterface {
  const initialize = async () => {
    if (isDatadogInitialized) {
      return;
    }

    const sdk = await loadDatadogBrowserSdk();
    rum = sdk.rum;
    logs = sdk.logs;

    const widgetApp = resolveWidgetApp();

    const baseDDConfig = {
      applicationId: widgetApp?.applicationId ?? applicationId,
      clientToken: widgetApp?.clientToken ?? clientToken,
      env,
      version: ExpoConstants.expoConfig?.version,
      sessionSampleRate: 100,
      site: DATADOG_SITE,
    };

    rum.init({
      ...baseDDConfig,
      ...(widgetApp ? { sessionReplaySampleRate: 0 } : {}),
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      service: widgetApp ? WIDGET_SERVICE : FLAGSHIP_SERVICE,
      allowedTracingUrls: [getEnvironmentConfig().apiBaseUrl],
    });
    logs.init(baseDDConfig);

    isDatadogInitialized = true;
  };

  const setUserId: TrackingInterface['setUserId'] = (userId, context) => {
    if (!isDatadogInitialized || !rum || !logs) {
      return;
    }

    const { type, anonymousId, ...extraInfo } = context ?? {};

    if (type === 'user') {
      logs.setUser({ id: userId, ...extraInfo });
      rum.setUser({ id: userId, ...extraInfo });
    } else if (anonymousId) {
      logs.setUser({
        id: anonymousId ?? userId,
        anonymous_id: anonymousId ?? userId,
      });
      rum.setUser({
        id: anonymousId ?? userId,
        anonymous_id: anonymousId ?? userId,
      });

      rum.setGlobalContext({
        user: {
          anonymousId: anonymousId ?? userId,
        },
      });
    }
  };

  const setTags: TrackingInterface['setTags'] = (tags) => {
    if (!isDatadogInitialized || !rum || !logs) {
      return;
    }
    Object.entries(tags).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }
      rum?.setGlobalContextProperty(key, value);
      logs?.setGlobalContextProperty(key, value);
    });
  };

  const trackScreenName: TrackingInterface['trackScreenName'] = (
    name,
    params,
  ) => {
    if (!isDatadogInitialized || !rum) {
      return;
    }

    rum.startView({
      name,
      context: params,
    });
  };

  return Object.freeze({
    initialize,
    setUserId,
    setTags,
    trackScreenName,
  });
}
